// 016A — Test de Valoración de Edad Gestacional (Capurro + Ballard), fila
// Fase 5 del roadmap (ver PLAN.md "FASE 5"). No es un formulario de campos
// sino una calculadora de 2 escalas somático/neuromusculares transcritas
// verbatim del PDF oficial (`SNS-MSP/HCU-form.016A/2021`, página 70):
//
// - Capurro (somático): 5 ítems graduados → suma → fórmula impresa
//   `Edad Gestacional = (204 + Total Puntaje) / 7` (días → semanas).
// - Ballard: madurez neuromuscular (6 ítems) + madurez física (6 ítems, uno
//   de ellos — genitales — depende del sexo del paciente, ver
//   `definicionBallardFisica`) → índice de madurez combinado → tabla de
//   conversión impresa (13 puntos, -10 a 50 → 20 a 44 semanas).
//
// Ambas escalas reusan el motor `EscalaClinica` (items/opciones/total, sin
// bandas de interpretación — el resultado de cada una es una fórmula
// continua, no una categoría). Las filas cuyo criterio en el papel es un
// ícono/dibujo sin texto (Postura, Signo de la Bufanda, Talón-Oreja de
// Ballard) se etiquetan por su puntaje impreso ("Puntaje N") en vez de
// inventar una descripción clínica que el propio Anexo no imprime —
// "equivalencia funcional, no facsímil", mismo criterio ya usado para el
// selector de tamaño de pupila de 115.
import type { Encounter, Observation, ObservationComponent, Patient, Reference } from '@medplum/fhirtypes';
import type { Par } from './index';
import { calcularTotalEscala, paresDeEscalaClinica, type DefinicionEscalaClinica, type ItemEscalaClinica, type RespuestasEscalaClinica } from './uci/EscalaClinica';
import { VITAL_SIGNS_CATEGORY } from './vitalSigns';

export type SexoBiologico = 'male' | 'female';

/** Mismo LOINC que ya usa `Formulario034.tsx`/`curvaCrecimiento.tsx` para "edad gestacional" — acá es la del recién nacido, calculada por Capurro o Ballard. */
export const EDAD_GESTACIONAL_LOINC = '49051-6';
export const SISTEMA_EDAD_GESTACIONAL = 'http://logiacare.ec/fhir/edad-gestacional-metodo';

// ---------- Test de Capurro (somático) ----------

export const DEFINICION_CAPURRO: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'formaOreja',
      etiqueta: 'Forma de la oreja',
      opciones: [
        { valor: 0, etiqueta: 'Chata, deforme, pabellón no incurvado' },
        { valor: 8, etiqueta: 'Pabellón parcialmente incurvado, borde superior' },
        { valor: 16, etiqueta: 'Pabellón incurvado, borde superior' },
        { valor: 24, etiqueta: 'Pabellón incurvado total' },
      ],
    },
    {
      clave: 'glandulaMamaria',
      etiqueta: 'Tamaño de la glándula mamaria',
      opciones: [
        { valor: 0, etiqueta: 'No palpable' },
        { valor: 5, etiqueta: 'Palpable, menos de 5 mm' },
        { valor: 10, etiqueta: 'Palpable, entre 5 y 10 mm' },
        { valor: 15, etiqueta: 'Palpable, más de 10 mm' },
      ],
    },
    {
      clave: 'pezon',
      etiqueta: 'Formación del pezón',
      opciones: [
        { valor: 0, etiqueta: 'Apenas visible, sin areola' },
        { valor: 5, etiqueta: 'Diámetro menor a 7.5 mm, areola lisa y chata' },
        { valor: 10, etiqueta: 'Diámetro mayor a 7.5 mm, areola punteada, borde no levantado' },
        { valor: 15, etiqueta: 'Diámetro mayor a 7.5 mm, areola punteada, borde levantado' },
      ],
    },
    {
      clave: 'texturaPiel',
      etiqueta: 'Textura de la piel',
      opciones: [
        { valor: 0, etiqueta: 'Muy fina, gelatinosa' },
        { valor: 5, etiqueta: 'Fina, lisa' },
        { valor: 10, etiqueta: 'Más gruesa, descamación superficial discreta' },
        { valor: 15, etiqueta: 'Gruesa, grietas superficiales, descamación de manos y pies' },
        { valor: 20, etiqueta: 'Gruesa, grietas profundas, apergaminada' },
      ],
    },
    {
      clave: 'pliegesPlantares',
      etiqueta: 'Pliegues plantares',
      opciones: [
        { valor: 0, etiqueta: 'Sin pliegues' },
        { valor: 5, etiqueta: 'Marcas mal definidas en la mitad anterior' },
        { valor: 10, etiqueta: 'Marcas definidas en la mitad anterior, surcos en el tercio anterior' },
        { valor: 15, etiqueta: 'Surcos en la mitad anterior' },
        { valor: 20, etiqueta: 'Surcos en más de la mitad anterior' },
      ],
    },
  ],
  // Sin bandas: el resultado impreso es una fórmula continua sobre el total, no una categoría.
  rangos: [],
};

/** Fórmula impresa tal cual en el formulario: `(204 + Total Puntaje) / 7` — el resultado ya está en semanas (204 días = 29.14 semanas de base + el total en días/7). */
export function edadGestacionalCapurroEnSemanas(totalPuntaje: number): number {
  return (204 + totalPuntaje) / 7;
}

// ---------- Test de Ballard ----------

export const DEFINICION_BALLARD_NEUROMUSCULAR: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'postura',
      etiqueta: 'Postura',
      opciones: [
        { valor: 0, etiqueta: 'Puntaje 0' },
        { valor: 1, etiqueta: 'Puntaje 1' },
        { valor: 2, etiqueta: 'Puntaje 2' },
        { valor: 3, etiqueta: 'Puntaje 3' },
        { valor: 4, etiqueta: 'Puntaje 4' },
      ],
    },
    {
      clave: 'ventanaCuadrada',
      etiqueta: 'Ventana cuadrada (muñeca)',
      opciones: [
        { valor: -1, etiqueta: 'Mayor a 90°' },
        { valor: 0, etiqueta: '90°' },
        { valor: 1, etiqueta: '60°' },
        { valor: 2, etiqueta: '45°' },
        { valor: 3, etiqueta: '30°' },
        { valor: 4, etiqueta: '0°' },
      ],
    },
    {
      clave: 'reboteBrazos',
      etiqueta: 'Rebote de brazos',
      opciones: [
        { valor: 0, etiqueta: '180°' },
        { valor: 1, etiqueta: '140° a 180°' },
        { valor: 2, etiqueta: '110° a 140°' },
        { valor: 3, etiqueta: '90° a 110°' },
        { valor: 4, etiqueta: 'Menor a 90°' },
      ],
    },
    {
      clave: 'anguloPopliteo',
      etiqueta: 'Ángulo poplíteo',
      opciones: [
        { valor: -1, etiqueta: '180°' },
        { valor: 0, etiqueta: '160°' },
        { valor: 1, etiqueta: '140°' },
        { valor: 2, etiqueta: '120°' },
        { valor: 3, etiqueta: '100°' },
        { valor: 4, etiqueta: '90°' },
        { valor: 5, etiqueta: 'Menor a 90°' },
      ],
    },
    {
      clave: 'signoBufanda',
      etiqueta: 'Signo de la bufanda',
      opciones: [
        { valor: -1, etiqueta: 'Puntaje -1' },
        { valor: 0, etiqueta: 'Puntaje 0' },
        { valor: 1, etiqueta: 'Puntaje 1' },
        { valor: 2, etiqueta: 'Puntaje 2' },
        { valor: 3, etiqueta: 'Puntaje 3' },
        { valor: 4, etiqueta: 'Puntaje 4' },
      ],
    },
    {
      clave: 'talonOreja',
      etiqueta: 'Talón-oreja',
      opciones: [
        { valor: -1, etiqueta: 'Puntaje -1' },
        { valor: 0, etiqueta: 'Puntaje 0' },
        { valor: 1, etiqueta: 'Puntaje 1' },
        { valor: 2, etiqueta: 'Puntaje 2' },
        { valor: 3, etiqueta: 'Puntaje 3' },
        { valor: 4, etiqueta: 'Puntaje 4' },
      ],
    },
  ],
  rangos: [],
};

const ITEM_BALLARD_PIEL: ItemEscalaClinica = {
  clave: 'piel',
  etiqueta: 'Piel',
  opciones: [
    { valor: -1, etiqueta: 'Pegajosa, quebradiza, transparente' },
    { valor: 0, etiqueta: 'Gelatinosa, roja, traslúcida' },
    { valor: 1, etiqueta: 'Rosa pálido, venas visibles' },
    { valor: 2, etiqueta: 'Descamación superficial y/o eritema, pocas venas' },
    { valor: 3, etiqueta: 'Áreas pálidas y agrietadas, venas raras' },
    { valor: 4, etiqueta: 'Engrosamiento, agrietado profundo, no venas' },
    { valor: 5, etiqueta: 'Dura, agrietada y arrugada' },
  ],
};

/**
 * Corregido contra el PDF oficial (016A, pág. 70): la fila Lanugo puntúa de -1
 * a 4 (Ausente = -1 … Casi no hay lanugo = 4), no de 0 a 5. Antes cada opción
 * estaba desplazada +1 y el índice de madurez quedaba sobreestimado en 1 punto
 * (0,4 semanas) por cada recién nacido evaluado.
 *
 * Datos ya guardados: de este ítem NO se persiste la respuesta individual, solo
 * la `Observation` LOINC 49051-6 con las semanas ya calculadas (y el PDF con
 * texto). Esas semanas se siguen leyendo tal cual, sin reescribir ni migrar
 * (no hay forma de saber qué opción de lanugo se eligió); solo las evaluaciones
 * nuevas usan la escala corregida.
 */
const ITEM_BALLARD_LANUGO: ItemEscalaClinica = {
  clave: 'lanugo',
  etiqueta: 'Lanugo',
  opciones: [
    { valor: -1, etiqueta: 'Ausente' },
    { valor: 0, etiqueta: 'Escaso' },
    { valor: 1, etiqueta: 'Abundante' },
    { valor: 2, etiqueta: 'Fino' },
    { valor: 3, etiqueta: 'Áreas libres' },
    { valor: 4, etiqueta: 'Casi no hay lanugo' },
  ],
};

const ITEM_BALLARD_SUPERFICIE_PLANTAR: ItemEscalaClinica = {
  clave: 'superficiePlantar',
  etiqueta: 'Superficie plantar',
  opciones: [
    { valor: -2, etiqueta: 'Talón-dedo gordo, menor a 40 mm' },
    { valor: -1, etiqueta: 'Talón-dedo gordo, entre 40 y 50 mm' },
    { valor: 0, etiqueta: 'Mayor a 50 mm, no hay pliegues' },
    { valor: 1, etiqueta: 'Marcas rojas mortecinas' },
    { valor: 2, etiqueta: 'Sólo pliegues transversos anteriores' },
    { valor: 3, etiqueta: 'Pliegues en los 2/3 anteriores' },
    { valor: 4, etiqueta: 'Los pliegues cubren toda la planta' },
  ],
};

const ITEM_BALLARD_MAMAS: ItemEscalaClinica = {
  clave: 'mamas',
  etiqueta: 'Mamas',
  opciones: [
    { valor: -1, etiqueta: 'Imperceptibles' },
    { valor: 0, etiqueta: 'Apenas perceptibles' },
    { valor: 1, etiqueta: 'Areola plana, no hay glándula' },
    { valor: 2, etiqueta: 'Areola granulosa, glándula de 1 a 2 mm' },
    { valor: 3, etiqueta: 'Areola sobreelevada, glándula de 3 a 4 mm' },
    { valor: 4, etiqueta: 'Areola completa, glándula de 5 a 10 mm' },
  ],
};

const ITEM_BALLARD_OJO_OREJA: ItemEscalaClinica = {
  clave: 'ojoOreja',
  etiqueta: 'Ojo / oreja',
  opciones: [
    { valor: -2, etiqueta: 'Párpados fusionados fuertemente' },
    { valor: -1, etiqueta: 'Párpados fusionados levemente' },
    { valor: 0, etiqueta: 'Párpados abiertos, pabellón liso, permanece arrugado' },
    { valor: 1, etiqueta: 'Pabellón ligeramente incurvado, blando, despliegue lento' },
    { valor: 2, etiqueta: 'Pabellón bien incurvado, blando pero con despliegue rápido' },
    { valor: 3, etiqueta: 'Formado y firme, despliegue instantáneo' },
    { valor: 4, etiqueta: 'Cartílago grueso, oreja firme' },
  ],
};

const ITEM_BALLARD_GENITALES_MASCULINO: ItemEscalaClinica = {
  clave: 'genitales',
  etiqueta: 'Genitales (masculino)',
  opciones: [
    { valor: -1, etiqueta: 'Escroto aplanado, liso' },
    { valor: 0, etiqueta: 'Escroto vacío, con vagas rugosidades' },
    { valor: 1, etiqueta: 'Testículos en la parte alta del conducto, pliegues muy escasos' },
    { valor: 2, etiqueta: 'Testículos en descenso, rugosidades escasas' },
    { valor: 3, etiqueta: 'Testículos descendidos, rugosidades escasas' },
    { valor: 4, etiqueta: 'Testículos colgantes, rugosidades profundas' },
  ],
};

const ITEM_BALLARD_GENITALES_FEMENINO: ItemEscalaClinica = {
  clave: 'genitales',
  etiqueta: 'Genitales (femenino)',
  opciones: [
    { valor: -1, etiqueta: 'Clítoris prominente, labios aplanados' },
    { valor: 0, etiqueta: 'Clítoris prominente y labios menores pequeños' },
    { valor: 1, etiqueta: 'Clítoris prominente y labios menores de mayor tamaño' },
    { valor: 2, etiqueta: 'Labios mayores y menores igualmente prominentes' },
    { valor: 3, etiqueta: 'Labios mayores grandes, labios menores pequeños' },
    { valor: 4, etiqueta: 'Los labios mayores cubren el clítoris y los labios menores' },
  ],
};

/** El único ítem de "madurez física" que depende del sexo del paciente — sin sexo registrado (male/female) no hay forma de saber cuál de los 2 mostrar, así que Ballard-física queda sin definición posible (ver `Formulario016A.tsx`). */
export function definicionBallardFisica(sexo: SexoBiologico): DefinicionEscalaClinica {
  return {
    items: [
      ITEM_BALLARD_PIEL,
      ITEM_BALLARD_LANUGO,
      ITEM_BALLARD_SUPERFICIE_PLANTAR,
      ITEM_BALLARD_MAMAS,
      ITEM_BALLARD_OJO_OREJA,
      sexo === 'male' ? ITEM_BALLARD_GENITALES_MASCULINO : ITEM_BALLARD_GENITALES_FEMENINO,
    ],
    rangos: [],
  };
}

/** Tabla oficial impresa ("Índice de Madurez", 13 puntos) — se deja exportada para test: confirma que es exactamente lineal (paso 5 en el valor → paso 2 en semanas), así que `edadGestacionalBallardEnSemanas` calcula con la recta en vez de buscar en la tabla, sin extrapolar ni inventar nada que no esté ya implícito en los 13 puntos impresos. */
export const INDICE_MADUREZ_BALLARD: readonly { readonly valor: number; readonly semanas: number }[] = [
  { valor: -10, semanas: 20 },
  { valor: -5, semanas: 22 },
  { valor: 0, semanas: 24 },
  { valor: 5, semanas: 26 },
  { valor: 10, semanas: 28 },
  { valor: 15, semanas: 30 },
  { valor: 20, semanas: 32 },
  { valor: 25, semanas: 34 },
  { valor: 30, semanas: 36 },
  { valor: 35, semanas: 38 },
  { valor: 40, semanas: 40 },
  { valor: 45, semanas: 42 },
  { valor: 50, semanas: 44 },
];

/** `semanas = 24 + 0.4 × índice` — verificado contra los 13 puntos de `INDICE_MADUREZ_BALLARD` (test dedicado). */
export function edadGestacionalBallardEnSemanas(indiceMadurezTotal: number): number {
  return 24 + 0.4 * indiceMadurezTotal;
}

// ---------- Estado combinado ----------

export interface EstadoEdadGestacional {
  readonly capurro: RespuestasEscalaClinica;
  readonly ballardNeuromuscular: RespuestasEscalaClinica;
  readonly ballardFisica: RespuestasEscalaClinica;
}

export function estadoInicialEdadGestacional(): EstadoEdadGestacional {
  return { capurro: {}, ballardNeuromuscular: {}, ballardFisica: {} };
}

/** `undefined` si falta cualquiera de los 2 grupos (neuromuscular + física) — mismo criterio de "sin total parcial inventado" que el resto del motor. */
export function indiceMadurezBallard(sexo: SexoBiologico | undefined, estado: EstadoEdadGestacional): number | undefined {
  if (sexo !== 'male' && sexo !== 'female') return undefined;
  const totalNeuromuscular = calcularTotalEscala(DEFINICION_BALLARD_NEUROMUSCULAR, estado.ballardNeuromuscular);
  const totalFisica = calcularTotalEscala(definicionBallardFisica(sexo), estado.ballardFisica);
  if (totalNeuromuscular === undefined || totalFisica === undefined) return undefined;
  return totalNeuromuscular + totalFisica;
}

function componenteMetodo(metodo: 'Capurro' | 'Ballard'): ObservationComponent {
  return { code: { coding: [{ system: SISTEMA_EDAD_GESTACIONAL, code: 'metodo' }] }, valueString: metodo };
}

/**
 * Un `Observation` (LOINC 49051-6) por cada método efectivamente completo —
 * el formulario permite cargar ambos de forma independiente (son 2
 * mediciones reales, no una elige a la otra), cada uno tageado con su
 * método vía `component`, mismo patrón que `escalasRecuperacionAnestesicaDatos.ts`.
 *
 * Nota para quien construya 016-Bloque D o 016B/016C (ver PLAN.md, Fase 5):
 * si van a leer "la" edad gestacional de este formulario como la Observation
 * LOINC 49051-6 más reciente (patrón de `curvaPercentilPorEdad.ts` para otros
 * LOINC), deben filtrar por el `component` de `metodo` — con ambos tests
 * cargados el mismo `effectiveDateTime` produce 2 Observation, y sin filtrar
 * el resultado sería no determinístico.
 */
export function observacionesEdadGestacional(
  subject: Reference<Patient>,
  encounter: Reference<Encounter> | undefined,
  sexo: SexoBiologico | undefined,
  estado: EstadoEdadGestacional,
  efectivo: string,
): Observation[] {
  const observaciones: Observation[] = [];
  const comunes = { subject, encounter, status: 'final' as const, effectiveDateTime: efectivo, category: [VITAL_SIGNS_CATEGORY] };

  const totalCapurro = calcularTotalEscala(DEFINICION_CAPURRO, estado.capurro);
  if (totalCapurro !== undefined) {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: 'http://loinc.org', code: EDAD_GESTACIONAL_LOINC, display: 'Edad gestacional' }] },
      valueQuantity: { value: edadGestacionalCapurroEnSemanas(totalCapurro), unit: 'semanas', system: 'http://unitsofmeasure.org', code: 'wk' },
      component: [componenteMetodo('Capurro')],
    });
  }

  const indiceBallard = indiceMadurezBallard(sexo, estado);
  if (indiceBallard !== undefined) {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: 'http://loinc.org', code: EDAD_GESTACIONAL_LOINC, display: 'Edad gestacional' }] },
      valueQuantity: { value: edadGestacionalBallardEnSemanas(indiceBallard), unit: 'semanas', system: 'http://unitsofmeasure.org', code: 'wk' },
      component: [componenteMetodo('Ballard')],
    });
  }

  return observaciones;
}

/** `Par[]` de respaldo para el PDF — mismos valores que `observacionesEdadGestacional`, sin depender de que el guardado ya haya ocurrido. */
export function paresDeEdadGestacional(sexo: SexoBiologico | undefined, estado: EstadoEdadGestacional): Par[] {
  const pares: Par[] = [...paresDeEscalaClinica(DEFINICION_CAPURRO, estado.capurro, 'Capurro — Subtotal')];
  const totalCapurro = calcularTotalEscala(DEFINICION_CAPURRO, estado.capurro);
  if (totalCapurro !== undefined) {
    pares.push({ pregunta: 'Edad gestacional (Capurro)', respuesta: `${edadGestacionalCapurroEnSemanas(totalCapurro).toFixed(1)} semanas` });
  }

  pares.push(...paresDeEscalaClinica(DEFINICION_BALLARD_NEUROMUSCULAR, estado.ballardNeuromuscular, 'Ballard — Madurez neuromuscular — Subtotal'));
  if (sexo === 'male' || sexo === 'female') {
    pares.push(...paresDeEscalaClinica(definicionBallardFisica(sexo), estado.ballardFisica, 'Ballard — Madurez física — Subtotal'));
  }
  const indiceBallard = indiceMadurezBallard(sexo, estado);
  if (indiceBallard !== undefined) {
    pares.push({ pregunta: 'Índice de madurez (Ballard)', respuesta: String(indiceBallard) });
    pares.push({ pregunta: 'Edad gestacional (Ballard)', respuesta: `${edadGestacionalBallardEnSemanas(indiceBallard).toFixed(1)} semanas` });
  }

  return pares.filter((p) => p.respuesta.trim() !== '');
}
